import { NumberUtil } from '../../util';
import _ from 'lodash';

/**
 * TestCase:
 *
 *
 * @param data
 * @param nullable
 * @param zeroDisplayAs0
 * @param mask
 * @param maxLength
 * @param debugMsg
 * @returns {{value: (number|*)}}
 */
export function normalizeNumberData(data, nullable, zeroDisplayAs0, mask, maxLength, debugMsg) {
    /**
     * 入参正确性检查
     */
    if (data != null && typeof data !== 'string' && typeof data !== 'number') {
        throw new Error(
            `${debugMsg},入参 data 只允许传入【null、string、number】类型值，实际传入【${data}, ${typeof data}】，请检查!`
        );
    }

    if (typeof nullable !== 'boolean') {
        throw new Error(
            `${debugMsg},入参 nullable 只允许传入【boolean】类型值，实际传入【${nullable}, ${typeof nullable}】，请检查!`
        );
    }

    if (typeof zeroDisplayAs0 !== 'boolean') {
        throw new Error(
            `${debugMsg},入参 zeroDisplayAs0 只允许传入【boolean】类型值，实际传入【${zeroDisplayAs0}, ${typeof zeroDisplayAs0}】，请检查!`
        );
    }

    if (typeof mask !== 'string') {
        throw new Error(
            `${debugMsg},入参 mask 只允许传入【string】类型值，实际传入【${mask}, ${typeof mask}】，请检查!`
        );
    }

    if (typeof maxLength !== 'number') {
        throw new Error(
            `${debugMsg},入参 maxLength 只允许传入【number】类型值，实际传入【${maxLength}, ${typeof maxLength}】，请检查!`
        );
    }

    let normalizedData;

    if (data == null) {
        if (nullable) {
            normalizedData = null;
        } else {
            normalizedData = 0;
        }
    } else if (typeof data === 'string') {
        if (data === '') {
            if (nullable) {
                normalizedData = null;
            } else {
                normalizedData = 0;
            }
        } else {
            normalizedData = stringToNumber(data);
            if (Number.isNaN(normalizedData)) {
                throw new Error(
                    `${debugMsg}，入参 data 为 string 类型，但值【${data}】无法转换为 number 型数值，请检查！`
                );
            }

            normalizedData = formatNumber(normalizedData, nullable, zeroDisplayAs0, mask, maxLength);
            normalizedData = stringToNumber(normalizedData);
        }
    } else if (typeof data === 'number') {
        normalizedData = formatNumber(data, nullable, zeroDisplayAs0, mask, maxLength);
        normalizedData = stringToNumber(normalizedData);
    } else {
        `${debugMsg},入参 data 只允许传入【null、string、number】类型值，实际传入【${data}, ${typeof data}】，请检查!`;
    }

    return normalizedData;
}

export function clearNumberData(nullable, zeroDisplayAs0, mask, maxLength, debugMsg) {
    return normalizeNumberData(null, nullable, zeroDisplayAs0, mask, maxLength, debugMsg);
}

export function canNumberDataPassRequiredTest(data, required, nullable, zeroDisplayAs0, debugMsg) {
    /**
     * 入参正确性检查
     */
    if (data != null && typeof data !== 'number') {
        throw new Error(
            `${debugMsg},入参 data 只允许传入【null、number】类型值，实际传入【${data}, ${typeof data}】，请检查!`
        );
    }

    if (typeof required !== 'boolean') {
        throw new Error(
            `${debugMsg},入参 required 只允许传入【boolean】类型值，实际传入【${required}, ${typeof required}】，请检查!`
        );
    }

    if (typeof nullable !== 'boolean') {
        throw new Error(
            `${debugMsg},入参 nullable 只允许传入【boolean】类型值，实际传入【${nullable}, ${typeof nullable}】，请检查!`
        );
    }

    if (typeof zeroDisplayAs0 !== 'boolean') {
        throw new Error(
            `${debugMsg},入参 zeroDisplayAs0 只允许传入【boolean】类型值，实际传入【${zeroDisplayAs0}, ${typeof zeroDisplayAs0}】，请检查!`
        );
    }

    if (!required) {
        return true;
    }

    if (nullable) {
        if (data == null) {
            return false;
        }

        if (!zeroDisplayAs0 && data === 0) {
            return false;
        }
    } else {
        if (!zeroDisplayAs0 && (data === 0 || data == null)) {
            return false;
        }
    }

    return true;
}

/**
 * stringToNumber
 *
 *  "" ==> 0
 *
 *
 * @param data
 * @returns {number}
 */
function stripDelimiters(value, delimiter) {
    const delimiterRE = new RegExp(delimiter.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'g');
    return value.replace(delimiterRE, '');
}

export function stringToNumber(data) {
    if (typeof data !== 'string') {
        throw new Error(
            `stringToNumber 入参 data 只允许传入【string】类型值，实际传入【${data}, ${typeof data}】，请检查!`
        );
    }

    if (!data) {
        return 0;
    }

    // 去掉千位分隔符","
    data = stripDelimiters(data, ',');

    if (data.indexOf('%') !== -1) {
        // 去掉百分号
        data = data.slice(0, -1);

        if (Number.isNaN(Number(data))) {
            return Number.NaN;
        }

        return parseFloat(data) / 100;
    } else {
        if (Number.isNaN(Number(data))) {
            return Number.NaN;
        }
        return parseFloat(data);
    }
}

/**
 * TODO：如果值太大怎么办？？？即出现科学计数法...
 *
 * 1. 四舍五入
 * 2. 千位分隔符
 * 3. 百分数
 * 4. 正数
 * 5. maxLength 截断
 * 6. mask匹配（尾 0 补齐）
 */
export function formatNumber(data, nullable, zeroDisplayAs0, mask, maxLength) {
    /**
     * 入参正确性检查
     */
    if (data != null && typeof data !== 'number') {
        throw new Error(
            `formatNumber 入参 data 只允许传入【null、number】类型值，实际传入【${data}, ${typeof data}】，请检查!`
        );
    }

    if (typeof nullable !== 'boolean') {
        throw new Error(
            `formatNumber 入参 nullable 只允许传入【boolean】类型值，实际传入【${nullable}, ${typeof nullable}】，请检查!`
        );
    }

    if (typeof zeroDisplayAs0 !== 'boolean') {
        throw new Error(
            `formatNumber 入参 zeroDisplayAs0 只允许传入【boolean】类型值，实际传入【${zeroDisplayAs0}, ${typeof zeroDisplayAs0}】，请检查!`
        );
    }

    if (typeof mask !== 'string') {
        throw new Error(
            `formatNumber 入参 mask 只允许传入【string】类型值，实际传入【${mask}, ${typeof mask}】，请检查!`
        );
    }

    if (typeof maxLength !== 'number') {
        throw new Error(
            `formatNumber 入参 maxLength 只允许传入【number】类型值，实际传入【${maxLength}, ${typeof maxLength}】，请检查!`
        );
    }

    if (typeof maxLength < -1) {
        throw new Error(
            `formatNumber 入参 maxLength 应该是  >= -1 的整数，实际传入【${maxLength}, ${typeof maxLength}】，请检查!`
        );
    }

    // 1. 如果 data == null && nullable == true ==> ""
    // 2. 如果 data === 0 && zeroDisplayAs0 == false ==> ""
    if (data == null && nullable === true) {
        return '';
    }

    // nullable === false 时， null 等价于 0
    if (data == null && nullable === false) {
        data = 0;
    }

    if (data === 0 && zeroDisplayAs0 === false) {
        return '';
    }

    // 3. 根据 mask 计算出需要保留几位小数
    let decimalPartMaxLength = 0;
    let integerPartMaxLength = Number.MAX_SAFE_INTEGER;
    if (mask.indexOf('.') > -1) {
        let t = stripDelimiters(mask, '%');
        t = t.split('.')[1];
        decimalPartMaxLength = t.length;
    }
    if (maxLength > -1) {
        integerPartMaxLength = maxLength - decimalPartMaxLength;
        if (integerPartMaxLength <= 0) {
            throw new Error('你配置的 maxLength 有点小啊，整数部分都截没了...');
        }
    }

    // MASK要求强制为正数
    if (mask.indexOf('+') > -1) {
        data = Math.abs(data);
    }

    // 有百分数
    let percentSign = null;
    if (mask.indexOf('%') > -1) {
        data = data * 100;
        percentSign = true;
    }

    // 四舍五入
    const numRounded = NumberUtil.round(data, decimalPartMaxLength);

    // 转换为字符串
    const strRounded = String(numRounded);

    // 按 maxLength + mask 截断整数
    let strIntegerPartCutted = strRounded;
    const [integerPartMayIncludeMinusSign, decimalPart] = strRounded.split('.');
    const integerPartDigits = stripDelimiters(integerPartMayIncludeMinusSign, '-'); // 截断整数的时候..别把负号给算进去
    if (integerPartDigits.length > integerPartMaxLength) {
        strIntegerPartCutted = String(
            parseInt(
                Number(integerPartMayIncludeMinusSign) / Math.pow(10, integerPartDigits.length - integerPartMaxLength)
            )
        );
        if (decimalPart) {
            strIntegerPartCutted += '.' + decimalPart;
        }
    }

    // 补齐尾0
    const arr = strIntegerPartCutted.split('.');
    let strSubFixAdded = null;
    if (arr.length === 1) {
        strSubFixAdded = `${arr[0]}`;
        if (decimalPartMaxLength) {
            strSubFixAdded += `.${_.padEnd('', decimalPartMaxLength, '0')}`;
        }
    } else if (arr.length === 2) {
        strSubFixAdded = `${arr[0]}.${_.padEnd(arr[1], decimalPartMaxLength, '0')}`;
    } else {
        throw new Error('不应该跑到这个地方...');
    }

    // 增加千位分隔符
    let thousandsSeparatorAdded = strSubFixAdded;
    if (mask.indexOf(',') > -1) {
        let [integerPartMayIncludeMinusSign, decimalPart] = thousandsSeparatorAdded.split('.');
        const p = /(\d+)(\d{3})/;
        while (p.test(integerPartMayIncludeMinusSign)) {
            integerPartMayIncludeMinusSign = integerPartMayIncludeMinusSign.replace(p, '$1' + ',' + '$2');
        }
        thousandsSeparatorAdded = integerPartMayIncludeMinusSign;
        if (decimalPart) {
            thousandsSeparatorAdded += '.' + decimalPart;
        }
    }

    if (percentSign) {
        return thousandsSeparatorAdded + '%';
    } else {
        return thousandsSeparatorAdded;
    }
}
